import { Link } from "react-router-dom";
import {
    Heart,
    Sparkles,
    MessageCircle,
    Compass,
    HandHeart,
    ArrowRight,
    GraduationCap,
    Brain,
    Users,
} from "lucide-react";

const RAY_PHOTO = "/ray-portrait.jpeg";

const reasons = [
    {
        icon: Heart,
        title: "Warm & empathetic",
        body: "You'll feel heard — not judged. Ray leads with genuine care, not a clipboard.",
    },
    {
        icon: Sparkles,
        title: "Experienced with addiction & anxiety",
        body: "Thoughtful, informed support for substance use, relapse prevention, and anxiety.",
    },
    {
        icon: MessageCircle,
        title: "Non-judgmental",
        body: "Whatever you bring to the room is met with openness. There's nothing too small, too hard, or too messy.",
    },
    {
        icon: HandHeart,
        title: "Collaborative, not corrective",
        body: "Ray won't try to 'fix' you. Together, we'll understand what's underneath and build from there.",
    },
    {
        icon: Compass,
        title: "Teens & adults through life transitions",
        body: "From identity shifts to relationship changes, from early recovery to re-invention — Ray meets you where you are.",
    },
];

const whatSheHelpsWith = [
    {
        icon: GraduationCap,
        title: "Teens navigating everyday life",
        body: "School stress, friendships, family, social media, identity — the day-in, day-out of figuring out who you are.",
    },
    {
        icon: Compass,
        title: "Finding yourself",
        body: "For teens and adults alike — the quieter work of knowing who you are underneath what you've been told.",
    },
    {
        icon: Users,
        title: "Life transitions",
        body: "New jobs, breakups, loss, recovery, parenthood, re-invention — the in-between seasons that ask a lot of you.",
    },
    {
        icon: Sparkles,
        title: "Addiction & recovery",
        body: "Substance use, early recovery, and relapse prevention — with practical tools and steady support.",
    },
    {
        icon: Brain,
        title: "Anxiety & overwhelm",
        body: "The racing thoughts, the pressure, the shutdowns — we'll work through what's driving it.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page">
            {/* HERO — ABOUT RAY (the person) */}
            <section className="pt-20 pb-12 md:pt-28 md:pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-5 gap-12 md:gap-16 items-start">
                        <div className="md:col-span-2 pd-fade-up">
                            <div className="relative rounded-2xl overflow-hidden border border-[#E3DCC8] shadow-sm aspect-[4/5] bg-[#F5F2EA]">
                                <img
                                    src={RAY_PHOTO}
                                    alt="Portrait of Ray Smith, LPC, therapist at Present Day Counseling and Recovery"
                                    className="w-full h-full object-cover"
                                    data-testid="ray-portrait"
                                />
                            </div>
                            <p className="mt-4 text-sm text-[#5B6358] italic">
                                Ray Smith, LPC, Therapist — she / her
                            </p>
                        </div>

                        <div className="md:col-span-3 pd-fade-up pd-delay-100 min-w-0">
                            <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                                About Ray
                            </p>
                            <h1 className="mt-5 font-heading text-3xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#2C332A] break-words">
                                A therapist who walks{" "}
                                <span className="italic text-[#8C9C88]">
                                    with you
                                </span>
                                , not ahead of you.
                            </h1>
                            <p
                                className="mt-4 text-sm tracking-[0.2em] uppercase text-[#5B6358] font-medium"
                                data-testid="about-credentials"
                            >
                                Ray Smith, LPC, Therapist
                            </p>

                            <div className="mt-8 space-y-6 text-lg text-[#5B6358] leading-relaxed">
                                <p>
                                    Hi, I'm Ray. My approach is simple: I help
                                    people understand themselves more fully,
                                    so the cycles that keep showing up can
                                    finally shift. We'll work together to see
                                    what's underneath — the patterns, the
                                    stories, the triggers — and build
                                    something steadier in their place.
                                </p>
                                <p className="text-[#2C332A] font-medium">
                                    I'm not here to fix you. I'm here to
                                    understand you — and to help you
                                    understand yourself.
                                </p>
                                <p>
                                    Therapy, at its best, is a partnership.
                                    You're the expert on your life. I bring
                                    the tools, the steady presence, and the
                                    willingness to sit with whatever is true
                                    for you right now.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT SHE DOES — clinical scope */}
            <section
                className="py-20 md:py-28"
                data-testid="about-what-she-does-section"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                            What Ray does
                        </p>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C332A] leading-tight">
                            Support for the{" "}
                            <span className="italic text-[#8C9C88]">
                                real
                            </span>{" "}
                            stuff.
                        </h2>
                        <p className="mt-6 text-lg text-[#5B6358] leading-relaxed">
                            Ray works with teens and adults across a range of
                            concerns — from clinical issues like addiction and
                            anxiety to the everyday weight of school, family,
                            and figuring out who you are.
                        </p>
                    </div>

                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {whatSheHelpsWith.map((item, i) => (
                            <div
                                key={item.title}
                                className="rounded-2xl p-8 bg-[#F5F2EA] border border-[#E3DCC8] transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-md"
                                data-testid={`what-she-does-card-${i}`}
                            >
                                <item.icon className="w-7 h-7 text-[#8C9C88]" />
                                <h3 className="mt-5 font-heading text-xl text-[#2C332A]">
                                    {item.title}
                                </h3>
                                <p className="mt-3 text-[#5B6358] leading-relaxed">
                                    {item.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* REASONS */}
            <section
                className="py-20 md:py-28 bg-[#F5F2EA] pd-grain"
                data-testid="about-reasons-section"
            >
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                            Why clients choose Ray
                        </p>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C332A] leading-tight">
                            Thoughtful, grounded,{" "}
                            <span className="italic text-[#8C9C88]">
                                human.
                            </span>
                        </h2>
                    </div>

                    <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {reasons.map((r, i) => (
                            <div
                                key={r.title}
                                className="rounded-2xl p-8 bg-white border border-[#E3DCC8] transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                                data-testid={`reason-card-${i}`}
                            >
                                <r.icon className="w-7 h-7 text-[#8C9C88]" />
                                <h3 className="mt-5 font-heading text-xl text-[#2C332A]">
                                    {r.title}
                                </h3>
                                <p className="mt-3 text-[#5B6358] leading-relaxed">
                                    {r.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24" data-testid="about-cta-section">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#2C332A]">
                        If this resonates, let's talk.
                    </h2>
                    <p className="mt-6 text-lg text-[#5B6358] leading-relaxed">
                        A free 15-minute consultation is the easiest way to see
                        if we're a fit — no pressure, no paperwork.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/consultation"
                            className="inline-flex items-center gap-2 rounded-full bg-[#8C9C88] text-white px-8 py-3.5 font-medium shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md"
                            data-testid="about-cta-book"
                        >
                            Book a Consultation
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
